import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { COUNTRIES as ALL_COUNTRIES } from "../lib/countries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth, fmtErr } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { Smartphone, Mail, ShieldCheck, Clock, Award, ArrowLeft, CheckCircle2, Eye, EyeOff, UserPlus, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, otpSend, otpVerify } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const patientOnly = params.get("as") === "patient";
  const adminOnly   = params.get("as") === "admin";
  const emailOnly   = params.get("as") === "email";  // escape-hatch for reviewers / legacy accounts
  const metaSource  = params.get("source") === "meta";

  const _forwardQS = () => {
    const keep = ["tag", "priority", "doctor", "scan"];
    const out = new URLSearchParams();
    keep.forEach((k) => {
      const v = params.get(k);
      if (v) out.set(k, v);
    });
    const s = out.toString();
    return s ? `?${s}` : "";
  };
  const patientLanding = () => `/patient${_forwardQS()}`;
  const routeByRole = (u) =>
    u.role === "admin" ? "/admin"
    : u.role === "doctor" ? "/doctor"
    : u.role === "hospital" ? "/hospital"
    : patientLanding();

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [phone, setPhone] = useState("");
  const [patientName, setPatientName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("IN");
  const [otp, setOtp] = useState("");
  const [sentOtp, setSentOtp] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back!");
      nav(routeByRole(u));
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const doSendOtp = async () => {
    if (!phone.match(/^\+?\d{8,15}$/)) { setErr("Enter a valid mobile number"); return; }
    setErr(""); setLoading(true);
    try {
      const r = await otpSend(phone);
      setSentOtp(r.otp_demo || "");
      if (r.sms) toast.success("OTP sent to your mobile via SMS");
      else toast.success(`OTP (demo): ${r.otp_demo}`);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  const doVerifyOtp = async () => {
    setErr(""); setLoading(true);
    try {
      const ageNum = age ? parseInt(age, 10) : null;
      await otpVerify(phone, otp, patientName, ageNum, gender || null, country || null);
      if (metaSource) {
        toast.success("🎉 Your FREE Second Opinion has been activated!");
        // The patient dashboard reads this flag on first mount and shows an
        // "Upload Medical Reports" CTA + confetti-style banner.
        try { sessionStorage.setItem("noi_meta_activated", "1"); } catch {}
      } else {
        toast.success("Welcome to NeuroOpinion India");
      }
      nav(patientLanding());
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const signupHref = `/signup/patient${_forwardQS()}`;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* LEFT — Form pane */}
      <div className="flex-1 flex flex-col px-5 sm:px-10 lg:px-16 py-8 lg:py-12">
        {/* Top: home link + brand */}
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4"/> Back to home
          </Link>
          <Link to="/" className="inline-flex items-center" data-testid="brand-logo">
            <img src="/icons/nav-brain.png" alt="NeuroOpinion India" className="w-9 h-9 sm:hidden object-contain" width={36} height={36} />
            <img src="/icons/wordmark.png" alt="NeuroOpinion India" className="hidden sm:block h-9 w-auto" height={36} />
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center mt-6 lg:mt-0">
          <div className="w-full max-w-md">
            {metaSource && (
              <div className="mb-4 rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 p-4" data-testid="meta-lead-welcome-banner">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 h-9 w-9 rounded-full bg-emerald-600 grid place-items-center text-white text-lg">🎉</div>
                  <div>
                    <div className="font-heading text-base font-bold text-emerald-900">
                      Welcome to NeuroOpinion!
                    </div>
                    <div className="text-xs text-emerald-800 mt-1">
                      Your <b>FREE Expert Brain & Spine Second Opinion</b> is waiting.
                      Just verify your mobile number below to activate it instantly.
                      No promo code needed.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-semibold tracking-wide">
              <ShieldCheck className="w-3 h-3"/> {adminOnly ? "Admin console — authorised only" : "Encrypted & private sign-in"}
            </div>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              {adminOnly ? "Admin sign-in." : metaSource ? "Activate your FREE opinion." : patientOnly ? "Sign up or sign in." : "Welcome back."}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {adminOnly
                ? "Sign in with your admin email & password to access the operations console."
                : emailOnly
                  ? "Email & password sign-in (for reviewer / legacy accounts). Regular patients & doctors should use mobile OTP."
                  : metaSource
                    ? "Verify your mobile OTP and we'll auto-match you to your Meta Ad registration — your FREE consultation activates on the next screen."
                    : patientOnly
                      ? "New patients: verify your mobile OTP and your account is created instantly — no separate signup needed."
                      : "Patients & doctors sign in with mobile OTP. Verify once — we'll route you to the right dashboard."}
            </p>

            {adminOnly ? (
              /* Admin-only flow — email + password, no tabs, no signup links */
              <form onSubmit={doLogin} className="mt-7 space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Admin email</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    data-testid="admin-login-email-input"
                    className="h-11 mt-1.5 border-slate-300"
                    placeholder="admin@neuroopinion.in"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("password")}</Label>
                    <Link to="/forgot-password" className="text-xs text-teal-700 font-semibold hover:underline" data-testid="admin-forgot-link">
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPwd ? "text" : "password"}
                      required
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      data-testid="admin-login-password-input"
                      className="h-11 pr-10 border-slate-300"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      data-testid="admin-login-password-toggle"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="admin-login-error">{err}</div>}
                <Button
                  disabled={loading}
                  type="submit"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-md"
                  data-testid="admin-login-submit-btn"
                >
                  {loading ? "Signing in…" : "Sign in to admin console"}
                </Button>
              </form>
            ) : emailOnly ? (
              /* Escape-hatch email/password flow — reviewer & legacy accounts only */
              <form onSubmit={doLogin} className="mt-7 space-y-4" data-testid="email-only-form">
                <div>
                  <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("email")}</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    data-testid="login-email-input"
                    className="h-11 mt-1.5 border-slate-300"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("password")}</Label>
                    <Link to="/forgot-password" className="text-xs text-teal-700 font-semibold hover:underline" data-testid="forgot-password-link">
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPwd ? "text" : "password"}
                      required
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      data-testid="login-password-input"
                      className="h-11 pr-10 border-slate-300"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      data-testid="login-password-toggle"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
                <Button
                  disabled={loading}
                  type="submit"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-md"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in…" : t("login")}
                </Button>
                <div className="text-xs text-slate-500 text-center pt-1">
                  Regular sign-in? <Link to="/login" className="text-teal-700 font-semibold hover:underline" data-testid="back-to-otp-link">Use mobile OTP instead</Link>
                </div>
              </form>
            ) : (
            <Tabs defaultValue="otp" className="mt-7">
              <TabsList className="grid grid-cols-1 w-full bg-slate-100 p-1 rounded-xl h-auto">
                <TabsTrigger
                  value="otp"
                  data-testid="tab-login-otp"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-teal-700 py-2.5 text-sm font-medium"
                >
                  <Smartphone className="w-4 h-4 mr-1.5"/> {patientOnly ? "Sign up with OTP" : "Sign in with Mobile OTP"}
                </TabsTrigger>
              </TabsList>

              {/* MOBILE OTP */}
              <TabsContent value="otp" className="mt-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Your name <span className="text-slate-400 normal-case font-normal">· optional</span></Label>
                    <Input
                      placeholder="e.g. Ramesh Kumar"
                      value={patientName}
                      onChange={e => setPatientName(e.target.value)}
                      data-testid="login-name-input"
                      className="h-11 mt-1.5 border-slate-300"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Age</Label>
                      <Input type="number" min={0} max={120} placeholder="e.g. 42" value={age}
                        onChange={e => setAge(e.target.value)} data-testid="login-age-input"
                        className="h-11 mt-1.5 border-slate-300"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Gender</Label>
                      <Select value={gender} onValueChange={setGender}>
                        <SelectTrigger data-testid="login-gender-select" className="h-11 mt-1.5 border-slate-300"><SelectValue placeholder="Select"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("phone")}</Label>
                    <Input placeholder="+91XXXXXXXXXX" value={phone}
                      onChange={e => setPhone(e.target.value)} data-testid="login-phone-input"
                      className="h-11 mt-1.5 border-slate-300"
                      inputMode="tel"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Country</Label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      data-testid="login-country-select"
                      className="w-full h-11 mt-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      {ALL_COUNTRIES.map((c) => (
                        <option key={c.iso} value={c.iso}>
                          {c.name} ({c.dial})
                        </option>
                      ))}
                    </select>
                    <div className="text-[11px] text-slate-500 mt-1">You can&apos;t change this later — pick carefully.</div>
                  </div>
                  <Button
                    onClick={doSendOtp}
                    disabled={loading}
                    variant="outline"
                    className="w-full h-11 border-teal-300 text-teal-700 hover:bg-teal-50 font-semibold"
                    data-testid="otp-send-btn"
                  >
                    {t("send_otp")}
                  </Button>
                  {sentOtp && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2" data-testid="otp-demo-hint">
                      Demo OTP: <b>{sentOtp}</b>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("otp")}</Label>
                    <Input
                      value={otp}
                      onChange={e => setOtp(e.target.value)}
                      data-testid="login-otp-input"
                      className="h-11 mt-1.5 border-slate-300 tracking-[0.3em] font-mono text-center text-lg"
                      maxLength={6}
                      inputMode="numeric"
                    />
                  </div>
                  {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
                  <Button
                    onClick={doVerifyOtp}
                    disabled={loading || !otp}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-md"
                    data-testid="otp-verify-btn"
                  >
                    {t("verify_otp")} &amp; continue
                  </Button>
                  <p className="text-xs text-slate-500 text-center">New here? Verifying the OTP automatically creates your patient account — no separate signup required.</p>
                  <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                    <b>📱 Booked via WhatsApp?</b> Use the same number you chatted with our bot — you&apos;ll see your case, doctor and report right after OTP verification.
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            )}

            {/* Footer helper links — only visible in default OTP mode */}
            {!adminOnly && !emailOnly && (
              <div className="mt-6 space-y-1.5 text-center">
                {!patientOnly && (
                  <p className="text-xs text-slate-500">
                    Doctor?{" "}
                    <Link to="/signup" className="text-teal-700 font-semibold hover:underline" data-testid="go-doctor-signup-link">Apply to join the network</Link>
                  </p>
                )}
                {!patientOnly && (
                  <p className="text-xs text-slate-500">
                    Hospital partner?{" "}
                    <Link to="/hospital/signup" className="text-teal-700 font-semibold hover:underline" data-testid="go-hospital-signup-link">Apply for B2B access</Link>
                  </p>
                )}
                <p className="text-[11px] text-slate-400 pt-1">
                  Reviewer / legacy account?{" "}
                  <Link to="/login?as=email" className="text-slate-500 hover:text-teal-700 hover:underline" data-testid="email-login-escape-link">Sign in with email &amp; password</Link>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="text-[11px] text-slate-400 text-center mt-8">
          © {new Date().getFullYear()} NeuroOpinion India · MCI / NMC verified specialists
        </div>
      </div>

      {/* RIGHT — Brand pane (starts below the top header row so the white NeuroOpinion logo strip sits above it) */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[40%] relative overflow-hidden bg-slate-900 text-white lg:mt-20 lg:rounded-tl-[2rem]">
        {/* Decorative gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-700 via-slate-900 to-slate-950" aria-hidden />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-teal-500/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-blue-500/15 blur-3xl" aria-hidden />
        <div className="absolute inset-0 opacity-[0.05] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" aria-hidden />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold tracking-wide backdrop-blur-sm">
              <ShieldCheck className="w-3 h-3 text-teal-300"/> India's trusted neuro platform
            </div>
            <h2 className="mt-6 font-heading text-3xl xl:text-4xl font-bold leading-[1.15] tracking-tight">
              Expert neuro &amp; spine<br/>second opinions —<br/>
              <span className="text-teal-300">delivered in 48 hours.</span>
            </h2>
            <p className="mt-5 text-sm xl:text-base text-white/70 leading-relaxed max-w-md">
              Access verified NIMHANS &amp; AIIMS-trained neurosurgeons and neurologists from anywhere in India.
            </p>
          </div>

          {/* Trust bullets */}
          <ul className="mt-10 space-y-3.5">
            {[
              { Icon: Award, label: "NIMHANS &amp; AIIMS alumni" },
              { Icon: Clock, label: "48-hour written second opinions" },
              { Icon: ShieldCheck, label: "End-to-end encrypted, HIPAA-aligned" },
            ].map((b) => (
              <li key={b.label} className="flex items-center gap-3 text-sm xl:text-base">
                <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 grid place-items-center text-teal-300 backdrop-blur-sm shrink-0">
                  <b.Icon className="w-4.5 h-4.5"/>
                </span>
                <span dangerouslySetInnerHTML={{ __html: b.label }} />
              </li>
            ))}
          </ul>

          {/* Stats footer card — NIMHANS/AIIMS credentials replace vanity numbers */}
          <div className="mt-10 grid grid-cols-3 gap-4 p-5 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10">
            <Stat n="NIMHANS" l="&amp; AIIMS alumni"/>
            <Stat n="24-48 hr" l="Turnaround SLA"/>
            <Stat n="₹1,000" l="Starting fee"/>
          </div>

          {/* Patient quote */}
          <div className="mt-8 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-teal-300 shrink-0 mt-0.5"/>
            <blockquote className="text-xs xl:text-sm text-white/75 leading-relaxed italic">
              "Within 24 hours we had a clear written opinion from a NIMHANS-trained neurosurgeon. It saved us from an unnecessary surgery."
              <div className="mt-2 not-italic font-semibold text-white/90">— Ramesh K., Hyderabad</div>
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-heading text-lg xl:text-xl font-bold leading-none">{n}</div>
      <div className="text-[10px] xl:text-[11px] text-white/60 mt-1 leading-tight">{l}</div>
    </div>
  );
}
